'use client';

import { PropsWithClassname } from '@/app/types';
import { useTranslation } from 'react-i18next';

import { Project } from '@/app/api';
import Dialog from '@/app/components/Dialog/Dialog';
import { ShareInstagramDialog } from '@/app/components/ShareButtons/InstagramShare/InstagramShare';
import { PrintPosterDialog } from '@/app/components/ShareButtons/PrintPoster/PrintPoster';
import { ShareButtons } from '@/app/components/ShareButtons/ShareButtons';
import useAnalytics from '@/app/hooks/useAnalytics';
import { countryConfig } from '@/app/utils/countryConfig';
import { formatPrice } from '@/app/utils/formatting';
import { useState } from 'react';

interface ShareDialogProps extends PropsWithClassname {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
  defaultEmailValue: string;
  subProjectId?: string;
}

const HeartUtf8Icon = () => <code>&#129505;</code>;

const ShareDialog = ({ isOpen, onClose, className, project, defaultEmailValue, subProjectId }: ShareDialogProps) => {
  const { t } = useTranslation();
  const {
    projectDetail: { printPosterClicked },
  } = useAnalytics();

  const [isInstagramTutorialDialogOpen, setIsInstagramTutorialDialogOpen] = useState(false);
  const [isPrintPostalLabelDialogOpen, setIsPrintPostalLabelDialogOpen] = useState(false);

  const handlePrintPoster = () => {
    setIsPrintPostalLabelDialogOpen(true);
    printPosterClicked();
  };

  return (
    <>
      <Dialog isOpen={isOpen} onClose={onClose} className={className} title={t('components.ShareDialog.title')}>
        <div className="flex flex-col max-w-[360px] mx-auto">
          <p className="my-[16px]">
            {t('components.ShareDialog.description', {
              amountWithCurrency: formatPrice(countryConfig.shareDialogExtraEarnValue),
            })}{' '}
            <HeartUtf8Icon />
          </p>
          <ShareButtons
            project={project}
            handleInstagramShare={() => setIsInstagramTutorialDialogOpen(true)}
            handlePrintPoster={handlePrintPoster}
          />
        </div>
      </Dialog>
      <ShareInstagramDialog
        subProjectId={subProjectId}
        project={project}
        isOpen={isInstagramTutorialDialogOpen}
        onClose={() => setIsInstagramTutorialDialogOpen(false)}
      />
      <PrintPosterDialog
        project={project}
        defaultEmailValue={defaultEmailValue}
        isOpen={isPrintPostalLabelDialogOpen}
        onClose={() => setIsPrintPostalLabelDialogOpen(false)}
      />
    </>
  );
};

export default ShareDialog;
